import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import axios from 'axios';
import { 
  Layers, RotateCcw, ZoomIn, ZoomOut, Play, Pause, Info,
  CheckCircle2, Clock, Calendar, HelpCircle, DollarSign, Activity, FileText, Image as ImageIcon
} from 'lucide-react';

const BimViewer = () => {
  const mountRef = useRef(null);
  const [activeLayers, setActiveLayers] = useState({
    foundation: true,
    columns: true,
    slabs: true,
    walls: true,
  });
  
  const [selectedFloor, setSelectedFloor] = useState('all'); // 'all', '1', '2', '3'
  const [autoRotate, setAutoRotate] = useState(false);
  const [viewMode, setViewMode] = useState('status'); // 'status' (progress color), 'material' (realistic)
  const [loading, setLoading] = useState(true);

  // Loaded integrations
  const [ganttTasks, setGanttTasks] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [selectedElement, setSelectedElement] = useState(null);
  const [highlightedTaskId, setHighlightedTaskId] = useState(null);
  const [highlightedConceptCode, setHighlightedConceptCode] = useState(null);

  const activeProjectName = localStorage.getItem('activeProjectName') || 'Proyecto Sin Asignar';
  const activeProjectId = localStorage.getItem('activeProjectId');

  // Fetch Integrations
  useEffect(() => {
    const fetchIntegrations = async () => {
      if (!activeProjectId) return;
      try {
        const headers = {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'x-project-id': activeProjectId
        };
        const [ganttRes, catalogRes] = await Promise.all([
          axios.get('http://localhost:4000/api/gantt', { headers }),
          axios.get('http://localhost:4000/api/estimations/catalog', { headers })
        ]);
        setGanttTasks(ganttRes.data || []);
        setCatalog(catalogRes.data || []);
      } catch (err) {
        console.error("Error loading BIM integration data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchIntegrations();
  }, [activeProjectId]);

  // Elements definitions
  const elements = [
    // 1. FOUNDATION (Y = -0.25)
    { id: 'f1', type: 'foundation', floor: 1, geom: 'box', size: [8, 0.5, 8], pos: [0, -0.25, 0], ganttTaskName: 'Cimentación y Plantilla', conceptCode: 'CIM-02' },

    // 2. COLUMNS
    // Floor 1 Columns
    { id: 'c1_1', type: 'columns', floor: 1, geom: 'cylinder', size: [0.25, 0.25, 2], pos: [-3, 1, -3], ganttTaskName: 'Columnas y Muros de Contención', conceptCode: 'EST-01' },
    { id: 'c1_2', type: 'columns', floor: 1, geom: 'cylinder', size: [0.25, 0.25, 2], pos: [3, 1, -3], ganttTaskName: 'Columnas y Muros de Contención', conceptCode: 'EST-01' },
    { id: 'c1_3', type: 'columns', floor: 1, geom: 'cylinder', size: [0.25, 0.25, 2], pos: [-3, 1, 3], ganttTaskName: 'Columnas y Muros de Contención', conceptCode: 'EST-01' },
    { id: 'c1_4', type: 'columns', floor: 1, geom: 'cylinder', size: [0.25, 0.25, 2], pos: [3, 1, 3], ganttTaskName: 'Columnas y Muros de Contención', conceptCode: 'EST-01' },
    { id: 'c1_5', type: 'columns', floor: 1, geom: 'cylinder', size: [0.25, 0.25, 2], pos: [0, 1, 0], ganttTaskName: 'Columnas y Muros de Contención', conceptCode: 'EST-01' },

    // Floor 2 Columns
    { id: 'c2_1', type: 'columns', floor: 2, geom: 'cylinder', size: [0.25, 0.25, 2], pos: [-3, 3.25, -3], ganttTaskName: 'Columnas y Muros de Contención', conceptCode: 'EST-01' },
    { id: 'c2_2', type: 'columns', floor: 2, geom: 'cylinder', size: [0.25, 0.25, 2], pos: [3, 3.25, -3], ganttTaskName: 'Columnas y Muros de Contención', conceptCode: 'EST-01' },
    { id: 'c2_3', type: 'columns', floor: 2, geom: 'cylinder', size: [0.25, 0.25, 2], pos: [-3, 3.25, 3], ganttTaskName: 'Columnas y Muros de Contención', conceptCode: 'EST-01' },
    { id: 'c2_4', type: 'columns', floor: 2, geom: 'cylinder', size: [0.25, 0.25, 2], pos: [3, 3.25, 3], ganttTaskName: 'Columnas y Muros de Contención', conceptCode: 'EST-01' },

    // Floor 3 Columns
    { id: 'c3_1', type: 'columns', floor: 3, geom: 'cylinder', size: [0.22, 0.22, 2], pos: [-3, 5.5, -3], ganttTaskName: 'Columnas y Muros de Contención', conceptCode: 'EST-01' },
    { id: 'c3_2', type: 'columns', floor: 3, geom: 'cylinder', size: [0.22, 0.22, 2], pos: [3, 5.5, -3], ganttTaskName: 'Columnas y Muros de Contención', conceptCode: 'EST-01' },
    { id: 'c3_3', type: 'columns', floor: 3, geom: 'cylinder', size: [0.22, 0.22, 2], pos: [-3, 5.5, 3], ganttTaskName: 'Columnas y Muros de Contención', conceptCode: 'EST-01' },
    { id: 'c3_4', type: 'columns', floor: 3, geom: 'cylinder', size: [0.22, 0.22, 2], pos: [3, 5.5, 3], ganttTaskName: 'Columnas y Muros de Contención', conceptCode: 'EST-01' },

    // 3. SLABS
    { id: 's1', type: 'slabs', floor: 1, geom: 'box', size: [8.2, 0.2, 8.2], pos: [0, 2.1, 0], ganttTaskName: 'Losa de Entrepiso y Vaciado de Concreto', conceptCode: 'EST-03' },
    { id: 's2', type: 'slabs', floor: 2, geom: 'box', size: [8.2, 0.2, 8.2], pos: [0, 4.35, 0], ganttTaskName: 'Losa de Entrepiso y Vaciado de Concreto', conceptCode: 'EST-03' },
    { id: 's3', type: 'slabs', floor: 3, geom: 'box', size: [8.2, 0.2, 8.2], pos: [0, 6.6, 0], ganttTaskName: 'Losa de Entrepiso y Vaciado de Concreto', conceptCode: 'EST-03' },

    // 4. WALLS
    // Floor 1 Walls
    { id: 'w1_north', type: 'walls', floor: 1, geom: 'box', size: [7.8, 2, 0.2], pos: [0, 1, -3.9], ganttTaskName: 'Columnas y Muros de Contención', conceptCode: 'ALB-01' },
    { id: 'w1_south', type: 'walls', floor: 1, geom: 'box', size: [7.8, 2, 0.2], pos: [0, 1, 3.9], ganttTaskName: 'Columnas y Muros de Contención', conceptCode: 'ALB-01' },
    { id: 'w1_west', type: 'walls', floor: 1, geom: 'box', size: [0.2, 2, 7.8], pos: [-3.9, 1, 0], ganttTaskName: 'Columnas y Muros de Contención', conceptCode: 'ALB-01' },

    // Floor 2 Walls
    { id: 'w2_north', type: 'walls', floor: 2, geom: 'box', size: [7.8, 2, 0.2], pos: [0, 3.25, -3.9], ganttTaskName: 'Columnas y Muros de Contención', conceptCode: 'ALB-01' },
    { id: 'w2_west', type: 'walls', floor: 2, geom: 'box', size: [0.2, 2, 7.8], pos: [-3.9, 3.25, 0], ganttTaskName: 'Columnas y Muros de Contención', conceptCode: 'ALB-01' },
    { id: 'w2_east', type: 'walls', floor: 2, geom: 'box', size: [0.2, 2, 7.8], pos: [3.9, 3.25, 0], ganttTaskName: 'Columnas y Muros de Contención', conceptCode: 'ALB-01' },

    // Floor 3 Walls
    { id: 'w3_north', type: 'walls', floor: 3, geom: 'box', size: [7.8, 2, 0.2], pos: [0, 5.5, -3.9], ganttTaskName: 'Acabados, Yesos y Pintura', conceptCode: 'ALB-01' },
    { id: 'w3_south', type: 'walls', floor: 3, geom: 'box', size: [7.8, 2, 0.2], pos: [0, 5.5, 3.9], ganttTaskName: 'Acabados, Yesos y Pintura', conceptCode: 'ALB-01' }
  ];

  // Helper to determine element status based on linked concept or gantt task
  const getDynamicStatus = (el) => {
    // 1. Try to find the concept progress first
    const concept = catalog.find(c => c.code.startsWith(el.conceptCode));
    if (concept) {
      const contractQty = parseFloat(concept.contract_qty) || 1;
      const executedQty = parseFloat(concept.executed_qty) || 0;
      const progress = executedQty / contractQty;
      
      // Determine based on exact percentages
      // Slabs/Columns on Floor 3 might be planned even if Floor 1 is complete
      if (el.floor === 1) {
        if (progress >= 0.3) return 'completed';
        if (progress > 0) return 'inProgress';
      } else if (el.floor === 2) {
        if (progress >= 0.7) return 'completed';
        if (progress > 0.3) return 'inProgress';
      } else if (el.floor === 3) {
        if (progress >= 1.0) return 'completed';
        if (progress > 0.7) return 'inProgress';
      }
      return 'planned';
    }

    // 2. Fallback to Gantt Task Status
    const task = ganttTasks.find(t => t.name === el.ganttTaskName);
    if (task) {
      if (task.status === 'completed') return 'completed';
      if (task.status === 'active') return 'inProgress';
    }

    return 'planned';
  };

  // Three.js Scene Setup & Loop
  useEffect(() => {
    if (loading) return;

    const currentMount = mountRef.current;
    if (!currentMount) return;

    const width = currentMount.clientWidth;
    const height = currentMount.clientHeight;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    currentMount.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    scene.fog = new THREE.FogExp2(0x0f172a, 0.015);

    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 100);
    camera.position.set(12, 8, 12);
    camera.lookAt(0, 2, 0);

    const ambientLight = new THREE.AmbientLight(0xffffff, 0.4);
    scene.add(ambientLight);

    const dirLight = new THREE.DirectionalLight(0xffffff, 0.8);
    dirLight.position.set(10, 20, 10);
    dirLight.castShadow = true;
    dirLight.shadow.mapSize.width = 1024;
    dirLight.shadow.mapSize.height = 1024;
    scene.add(dirLight);

    const fillLight = new THREE.DirectionalLight(0x38bdf8, 0.3);
    fillLight.position.set(-10, 5, -10);
    scene.add(fillLight);

    const gridHelper = new THREE.GridHelper(30, 30, 0x334155, 0x1e293b);
    gridHelper.position.y = -0.5;
    scene.add(gridHelper);

    const objectsGroup = new THREE.Group();
    scene.add(objectsGroup);

    const colors = {
      completed: 0x10b981,
      inProgress: 0xf59e0b,
      planned: 0x475569,
      highlighted: 0x3b82f6, // Vibrant blue for active selection
      
      concrete: 0xa1a1aa,
      steel: 0x64748b,
      brick: 0xb45309,
      glass: 0x38bdf8
    };

    const getMaterial = (el, viewMode) => {
      const isSelected = selectedElement?.id === el.id;
      const isTaskHighlighted = highlightedTaskId && ganttTasks.find(t => t.id === highlightedTaskId)?.name === el.ganttTaskName;
      const isConceptHighlighted = highlightedConceptCode && el.conceptCode.startsWith(highlightedConceptCode);

      if (isSelected || isTaskHighlighted || isConceptHighlighted) {
        return new THREE.MeshStandardMaterial({ 
          color: colors.highlighted, 
          emissive: 0x1d4ed8,
          emissiveIntensity: 0.3,
          roughness: 0.3 
        });
      }

      if (viewMode === 'material') {
        switch(el.type) {
          case 'foundation': return new THREE.MeshStandardMaterial({ color: colors.concrete, roughness: 0.8 });
          case 'columns': return new THREE.MeshStandardMaterial({ color: colors.steel, metalness: 0.6, roughness: 0.3 });
          case 'slabs': return new THREE.MeshStandardMaterial({ color: colors.concrete, roughness: 0.7 });
          case 'walls': return new THREE.MeshStandardMaterial({ color: colors.brick, roughness: 0.9 });
          default: return new THREE.MeshStandardMaterial({ color: 0x94a3b8 });
        }
      } else {
        const status = getDynamicStatus(el);
        let color = colors.planned;
        if (status === 'completed') color = colors.completed;
        else if (status === 'inProgress') color = colors.inProgress;

        return new THREE.MeshStandardMaterial({ 
          color: color, 
          roughness: 0.5,
          transparent: status === 'planned',
          opacity: status === 'planned' ? 0.2 : 1.0
        });
      }
    };

    const renderedMeshes = [];

    const rebuildModel = () => {
      renderedMeshes.forEach(mesh => objectsGroup.remove(mesh));
      renderedMeshes.length = 0;

      elements.forEach(el => {
        if (!activeLayers[el.type]) return;
        if (selectedFloor !== 'all' && el.floor.toString() !== selectedFloor) return;

        let geometry;
        if (el.geom === 'box') {
          geometry = new THREE.BoxGeometry(...el.size);
        } else if (el.geom === 'cylinder') {
          geometry = new THREE.CylinderGeometry(el.size[0], el.size[1], el.size[2], 12);
        }

        const material = getMaterial(el, viewMode);
        const mesh = new THREE.Mesh(geometry, material);
        mesh.position.set(...el.pos);
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        
        // Save element link in userData for Raycasting clicks
        mesh.userData = { elementId: el.id };
        
        objectsGroup.add(mesh);
        renderedMeshes.push(mesh);
      });
    };

    rebuildModel();

    // Mouse Interaction / Raycasting setup
    let isDragging = false;
    let previousMousePosition = { x: 0, y: 0 };
    let spherical = { radius: 18, theta: Math.PI / 4, phi: Math.PI / 6 };

    const updateCameraPosition = () => {
      spherical.phi = Math.max(0.05, Math.min(Math.PI / 2 - 0.05, spherical.phi));
      camera.position.x = spherical.radius * Math.sin(spherical.theta) * Math.cos(spherical.phi);
      camera.position.y = spherical.radius * Math.sin(spherical.phi);
      camera.position.z = spherical.radius * Math.cos(spherical.theta) * Math.cos(spherical.phi);
      camera.lookAt(0, 2, 0);
    };

    updateCameraPosition();

    // Click Detection (Raycasting)
    const handleCanvasClick = (e) => {
      // Prevent raycasting during drags
      if (Math.abs(e.offsetX - previousMousePosition.x) > 3 || Math.abs(e.offsetY - previousMousePosition.y) > 3) {
        return;
      }
      
      const rect = renderer.domElement.getBoundingClientRect();
      const mouse = new THREE.Vector2(
        ((e.clientX - rect.left) / rect.width) * 2 - 1,
        -((e.clientY - rect.top) / rect.height) * 2 + 1
      );

      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera(mouse, camera);
      const intersects = raycaster.intersectObjects(objectsGroup.children);

      if (intersects.length > 0) {
        const clickedMesh = intersects[0].object;
        const clickedId = clickedMesh.userData.elementId;
        const matchedElement = elements.find(el => el.id === clickedId);
        
        if (matchedElement) {
          // Link Gantt task and concept catalog details
          const linkedTask = ganttTasks.find(t => t.name === matchedElement.ganttTaskName);
          const linkedConcept = catalog.find(c => c.code.startsWith(matchedElement.conceptCode));
          
          setSelectedElement({
            ...matchedElement,
            task: linkedTask || null,
            concept: linkedConcept || null
          });
          
          if (linkedTask) setHighlightedTaskId(linkedTask.id);
          if (linkedConcept) setHighlightedConceptCode(linkedConcept.code);
        }
      } else {
        setSelectedElement(null);
        setHighlightedTaskId(null);
        setHighlightedConceptCode(null);
      }
    };

    const handleMouseDown = (e) => { 
      isDragging = true; 
      previousMousePosition = { x: e.offsetX, y: e.offsetY };
    };
    
    const handleMouseMove = (e) => {
      if (isDragging) {
        const deltaMove = {
          x: e.offsetX - previousMousePosition.x,
          y: e.offsetY - previousMousePosition.y
        };
        spherical.theta -= deltaMove.x * 0.007;
        spherical.phi += deltaMove.y * 0.007;
        updateCameraPosition();
      }
      previousMousePosition = { x: e.offsetX, y: e.offsetY };
    };

    const handleMouseUp = (e) => { 
      handleCanvasClick(e);
      isDragging = false; 
    };

    const handleWheel = (e) => {
      e.preventDefault();
      spherical.radius += e.deltaY * 0.01;
      spherical.radius = Math.max(5, Math.min(30, spherical.radius));
      updateCameraPosition();
    };

    const domElement = renderer.domElement;
    domElement.addEventListener('mousedown', handleMouseDown);
    domElement.addEventListener('mousemove', handleMouseMove);
    domElement.addEventListener('mouseup', handleMouseUp);
    domElement.addEventListener('wheel', handleWheel, { passive: false });

    // Touch Support
    let touchStartDist = 0;
    const handleTouchStart = (e) => {
      if (e.touches.length === 1) {
        isDragging = true;
        previousMousePosition = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      } else if (e.touches.length === 2) {
        touchStartDist = Math.hypot(
          e.touches[0].clientX - e.touches[1].clientX,
          e.touches[0].clientY - e.touches[1].clientY
        );
      }
    };

    const handleTouchMove = (e) => {
      if (isDragging && e.touches.length === 1) {
        const deltaMove = {
          x: e.touches[0].clientX - previousMousePosition.x,
          y: e.touches[0].clientY - previousMousePosition.y
        };
        spherical.theta -= deltaMove.x * 0.008;
        spherical.phi += deltaMove.y * 0.008;
        updateCameraPosition();
        previousMousePosition = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      } else if (e.touches.length === 2) {
        const dist = Math.hypot(
          e.touches[0].clientX - e.touches[1].clientX,
          e.touches[0].clientY - e.touches[1].clientY
        );
        const factor = dist - touchStartDist;
        spherical.radius -= factor * 0.05;
        spherical.radius = Math.max(5, Math.min(30, spherical.radius));
        updateCameraPosition();
        touchStartDist = dist;
      }
    };

    const handleTouchEnd = () => { isDragging = false; };
    domElement.addEventListener('touchstart', handleTouchStart);
    domElement.addEventListener('touchmove', handleTouchMove);
    domElement.addEventListener('touchend', handleTouchEnd);

    // Render loop
    let animationFrameId;
    const animate = () => {
      animationFrameId = requestAnimationFrame(animate);
      if (autoRotate && !isDragging) {
        spherical.theta += 0.002;
        updateCameraPosition();
      }
      renderer.render(scene, camera);
    };
    animate();

    // Resize Observer
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const newW = entry.contentRect.width;
        const newH = entry.contentRect.height;
        renderer.setSize(newW, newH);
        camera.aspect = newW / newH;
        camera.updateProjectionMatrix();
      }
    });
    resizeObserver.observe(currentMount);

    return () => {
      cancelAnimationFrame(animationFrameId);
      resizeObserver.disconnect();
      domElement.removeEventListener('mousedown', handleMouseDown);
      domElement.removeEventListener('mousemove', handleMouseMove);
      domElement.removeEventListener('mouseup', handleMouseUp);
      domElement.removeEventListener('wheel', handleWheel);
      domElement.removeEventListener('touchstart', handleTouchStart);
      domElement.removeEventListener('touchmove', handleTouchMove);
      domElement.removeEventListener('touchend', handleTouchEnd);
      
      if (currentMount.contains(domElement)) {
        currentMount.removeChild(domElement);
      }
      renderer.dispose();
    };
  }, [loading, activeLayers, selectedFloor, viewMode, autoRotate, selectedElement, highlightedTaskId, highlightedConceptCode]);

  const toggleLayer = (layer) => {
    setActiveLayers(prev => ({ ...prev, [layer]: !prev[layer] }));
  };

  return (
    <div className="min-h-screen bg-theme-bg text-theme-text-primary p-4 md:p-8 font-sans">
      <div className="max-w-7xl mx-auto flex flex-col h-[calc(100vh-140px)]">
        
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight text-theme-text-primary mb-1 flex items-center gap-3">
              <Layers className="text-blue-500" size={32} /> Visor de Modelos 3D BIM (Integración GANTT & Estimaciones)
            </h1>
            <p className="text-theme-text-secondary text-sm font-medium">
              Vincular elementos constructivos con el cronograma del proyecto y reportes de avance físico-financiero de la obra.
            </p>
          </div>
          <div className="bg-theme-panel px-4 py-2 rounded-2xl border border-theme-border text-xs font-bold text-theme-text-secondary uppercase">
            Obra: {activeProjectName}
          </div>
        </div>

        {/* Workspace Layout */}
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-4 gap-6 min-h-0">
          
          {/* Left Column: Interactive lists & Controls */}
          <div className="lg:col-span-1 bg-theme-panel rounded-3xl border border-theme-border p-5 flex flex-col gap-6 overflow-y-auto">
            
            {/* Visual Layers Controls */}
            <div className="space-y-3">
              <span className="block text-xs font-black uppercase text-theme-text-secondary tracking-wider">Capas Estructurales</span>
              <div className="grid grid-cols-2 gap-2">
                <button 
                  onClick={() => toggleLayer('foundation')}
                  className={`p-2.5 rounded-xl border text-xs font-bold transition-all flex items-center gap-2 ${
                    activeLayers.foundation ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' : 'bg-theme-bg border-theme-border text-theme-text-secondary'
                  }`}
                >
                  <div className="h-2.5 w-2.5 rounded-full bg-emerald-500" /> Cimientos
                </button>
                <button 
                  onClick={() => toggleLayer('columns')}
                  className={`p-2.5 rounded-xl border text-xs font-bold transition-all flex items-center gap-2 ${
                    activeLayers.columns ? 'bg-blue-500/10 border-blue-500/30 text-blue-450' : 'bg-theme-bg border-theme-border text-theme-text-secondary'
                  }`}
                >
                  <div className="h-2.5 w-2.5 rounded-full bg-blue-500" /> Columnas
                </button>
                <button 
                  onClick={() => toggleLayer('slabs')}
                  className={`p-2.5 rounded-xl border text-xs font-bold transition-all flex items-center gap-2 ${
                    activeLayers.slabs ? 'bg-purple-500/10 border-purple-500/30 text-purple-400' : 'bg-theme-bg border-theme-border text-theme-text-secondary'
                  }`}
                >
                  <div className="h-2.5 w-2.5 rounded-full bg-purple-500" /> Losas
                </button>
                <button 
                  onClick={() => toggleLayer('walls')}
                  className={`p-2.5 rounded-xl border text-xs font-bold transition-all flex items-center gap-2 ${
                    activeLayers.walls ? 'bg-amber-500/10 border-amber-500/30 text-amber-400' : 'bg-theme-bg border-theme-border text-theme-text-secondary'
                  }`}
                >
                  <div className="h-2.5 w-2.5 rounded-full bg-amber-500" /> Muros
                </button>
              </div>
            </div>

            {/* Level selector */}
            <div>
              <span className="block text-xs font-black uppercase text-theme-text-secondary tracking-wider mb-2">Filtrar por Nivel</span>
              <div className="grid grid-cols-4 bg-theme-bg border border-theme-border rounded-xl p-1 text-xs">
                {['all', '1', '2', '3'].map((floor) => (
                  <button
                    key={floor}
                    onClick={() => setSelectedFloor(floor)}
                    className={`py-1.5 rounded-lg font-bold transition-all ${
                      selectedFloor === floor ? 'bg-blue-600 text-white shadow' : 'text-theme-text-secondary hover:text-theme-text-primary'
                    }`}
                  >
                    {floor === 'all' ? 'Todo' : `N${floor}`}
                  </button>
                ))}
              </div>
            </div>

            {/* Gantt Tasks Quick Select */}
            <div className="space-y-2.5 border-t border-theme-border pt-4">
              <span className="block text-xs font-black uppercase text-theme-text-secondary tracking-wider flex items-center gap-1">
                <Activity size={14} className="text-blue-500" /> Vínculo Gantt ({ganttTasks.length})
              </span>
              <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
                {ganttTasks.map(task => (
                  <button
                    key={task.id}
                    onClick={() => {
                      setHighlightedTaskId(highlightedTaskId === task.id ? null : task.id);
                      setHighlightedConceptCode(null);
                    }}
                    className={`w-full text-left p-2.5 rounded-xl border text-xs font-bold transition-all flex items-center justify-between ${
                      highlightedTaskId === task.id 
                        ? 'bg-blue-600/10 border-blue-500/40 text-blue-400 shadow-sm' 
                        : 'bg-theme-bg/50 border-theme-border/60 hover:bg-theme-bg text-theme-text-secondary hover:text-theme-text-primary'
                    }`}
                  >
                    <span className="truncate max-w-[80%]">{task.name}</span>
                    <span className="text-[10px] font-mono bg-theme-border px-1.5 py-0.5 rounded text-theme-text-secondary">{task.progress}%</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Estimations Quick Select */}
            <div className="space-y-2.5 border-t border-theme-border pt-4">
              <span className="block text-xs font-black uppercase text-theme-text-secondary tracking-wider flex items-center gap-1">
                <DollarSign size={14} className="text-green-500" /> Catálogo Estimado ({catalog.length})
              </span>
              <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                {catalog.map(concept => (
                  <button
                    key={concept.id}
                    onClick={() => {
                      setHighlightedConceptCode(highlightedConceptCode === concept.code ? null : concept.code);
                      setHighlightedTaskId(null);
                    }}
                    className={`w-full text-left p-2.5 rounded-xl border text-xs font-bold transition-all flex flex-col gap-1 ${
                      highlightedConceptCode === concept.code 
                        ? 'bg-blue-600/10 border-blue-500/40 text-blue-400 shadow-sm' 
                        : 'bg-theme-bg/50 border-theme-border/60 hover:bg-theme-bg text-theme-text-secondary hover:text-theme-text-primary'
                    }`}
                  >
                    <div className="flex justify-between w-full font-mono text-[10px]">
                      <span className="text-blue-500">{concept.code}</span>
                      <span className="text-zinc-500">{concept.unit}</span>
                    </div>
                    <span className="truncate text-theme-text-primary text-[11px] font-medium leading-snug">{concept.description}</span>
                  </button>
                ))}
              </div>
            </div>

          </div>

          {/* 3D WebGL Canvas Panel */}
          <div className="lg:col-span-2 bg-slate-950/90 rounded-3xl border border-theme-border relative overflow-hidden shadow-2xl flex flex-col">
            
            {/* View Mode Overlay */}
            <div className="absolute top-4 left-4 z-10 flex gap-2">
              <button 
                onClick={() => setAutoRotate(!autoRotate)}
                className={`p-2.5 rounded-xl border transition-all ${
                  autoRotate 
                    ? 'bg-blue-600 border-blue-500 text-white shadow-lg' 
                    : 'bg-slate-900/80 border-slate-700 text-slate-400 hover:text-white'
                }`}
                title="Girar Cámara Automáticamente"
              >
                {autoRotate ? <Pause size={18} /> : <Play size={18} />}
              </button>
              <div className="flex bg-slate-900/80 border border-slate-700 rounded-xl overflow-hidden p-0.5">
                <button
                  onClick={() => setViewMode('status')}
                  className={`px-3 py-1 text-xs font-bold rounded-lg transition-all ${
                    viewMode === 'status' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  Estatus
                </button>
                <button
                  onClick={() => setViewMode('material')}
                  className={`px-3 py-1 text-xs font-bold rounded-lg transition-all ${
                    viewMode === 'material' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  Materiales
                </button>
              </div>
            </div>

            {/* Legend Overlay */}
            {viewMode === 'status' && (
              <div className="absolute bottom-4 left-4 z-10 bg-slate-900/90 border border-slate-700/60 rounded-2xl p-4 backdrop-blur-md shadow-xl text-[10px] space-y-2">
                <div className="font-bold text-slate-300 uppercase tracking-widest text-[9px] mb-2 border-b border-slate-800 pb-1">Estatus 3D BIM</div>
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-md bg-[#10b981]"></div>
                  <span className="text-slate-400">Completado (100% Ejecutado)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-md bg-[#f59e0b]"></div>
                  <span className="text-slate-400">En Progreso (1% - 99% Ejecutado)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-md bg-[#475569]"></div>
                  <span className="text-slate-400">Planificado (0% Ejecutado)</span>
                </div>
              </div>
            )}

            {/* WebGL Canvas Container */}
            <div ref={mountRef} className="flex-1 w-full h-full cursor-grab active:cursor-grabbing" />
            
            <div className="absolute bottom-4 right-4 z-10 bg-slate-900/80 border border-slate-700/50 rounded-full px-3 py-1.5 backdrop-blur-sm text-[10px] text-slate-400 flex items-center gap-1.5 font-medium">
              <HelpCircle size={12} /> Clic para seleccionar elemento
            </div>

            {loading && (
              <div className="absolute inset-0 bg-slate-950 flex flex-col items-center justify-center text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
                <p className="text-sm font-semibold text-slate-300">Vinculando base de datos con modelo BIM...</p>
              </div>
            )}
          </div>

          {/* Right Column: Linked Integration Details */}
          <div className="lg:col-span-1 bg-theme-panel rounded-3xl border border-theme-border p-6 shadow-xl flex flex-col justify-between overflow-y-auto">
            {selectedElement ? (
              <div className="space-y-6">
                <div>
                  <div className="flex justify-between items-start">
                    <span className="text-[10px] font-black text-blue-500 uppercase tracking-widest block mb-1">Elemento Seleccionado</span>
                    <span className="text-[10px] font-bold bg-theme-border px-2 py-0.5 rounded-full text-theme-text-secondary uppercase">Nivel {selectedElement.floor}</span>
                  </div>
                  <h3 className="text-xl font-bold text-theme-text-primary flex items-center gap-2 mt-1">
                    <Layers size={18} className="text-blue-400" /> {selectedElement.type === 'foundation' ? 'Cimiento / Zapata' : selectedElement.type === 'columns' ? 'Columna / Marco' : selectedElement.type === 'slabs' ? 'Losa / Entrepiso' : 'Muro de Block'}
                  </h3>
                  <span className="text-[10px] font-mono text-zinc-500 block mt-0.5">UID: {selectedElement.id.toUpperCase()}</span>
                </div>

                {/* Dynamic Status Display */}
                <div className="bg-theme-bg/60 border border-theme-border rounded-2xl p-4 space-y-2">
                  <div className="text-xs font-bold text-theme-text-secondary uppercase">Progreso Físico BIM</div>
                  <div className="flex justify-between items-end mb-1">
                    <span className="text-2xl font-black text-theme-text-primary capitalize leading-none">
                      {getDynamicStatus(selectedElement) === 'completed' ? 'Completado' : getDynamicStatus(selectedElement) === 'inProgress' ? 'En Progreso' : 'Planificado'}
                    </span>
                    <span className="text-[10px] font-bold text-blue-400">
                      {getDynamicStatus(selectedElement) === 'completed' ? '100%' : getDynamicStatus(selectedElement) === 'inProgress' ? 'En Ejecución' : '0%'}
                    </span>
                  </div>
                </div>

                {/* Linked Gantt Task Detail */}
                {selectedElement.task ? (
                  <div className="space-y-3 border-t border-theme-border pt-4">
                    <span className="text-xs font-black uppercase text-theme-text-secondary tracking-wider flex items-center gap-1.5">
                      <Calendar size={14} className="text-blue-500" /> Tarea de Gantt Vinculada
                    </span>
                    <div className="p-3 bg-theme-bg border border-theme-border rounded-2xl space-y-2">
                      <div className="text-xs font-bold text-theme-text-primary leading-snug">{selectedElement.task.name}</div>
                      <div className="grid grid-cols-2 gap-2 text-[10px] text-theme-text-secondary">
                        <div>Inicio: <span className="font-semibold text-theme-text-primary">{new Date(selectedElement.task.start).toLocaleDateString()}</span></div>
                        <div>Término: <span className="font-semibold text-theme-text-primary">{new Date(selectedElement.task.end).toLocaleDateString()}</span></div>
                      </div>
                      <div className="space-y-1">
                        <div className="flex justify-between text-[10px]">
                          <span className="text-theme-text-secondary">Avance Tarea:</span>
                          <span className="font-bold text-theme-text-primary">{selectedElement.task.progress}%</span>
                        </div>
                        <div className="w-full h-1.5 bg-theme-border rounded-full overflow-hidden">
                          <div style={{ width: `${selectedElement.task.progress}%` }} className="h-full bg-blue-500 rounded-full" />
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-theme-text-secondary italic border-t border-theme-border pt-4">
                    Sin tarea de Gantt asociada.
                  </div>
                )}

                {/* Linked Estimation Concept Detail */}
                {selectedElement.concept ? (
                  <div className="space-y-3 border-t border-theme-border pt-4">
                    <span className="text-xs font-black uppercase text-theme-text-secondary tracking-wider flex items-center gap-1.5">
                      <DollarSign size={14} className="text-green-500" /> Concepto Financiero
                    </span>
                    <div className="p-3.5 bg-theme-bg border border-theme-border rounded-2xl space-y-3.5">
                      <div>
                        <span className="text-[10px] font-mono bg-blue-500/10 border border-blue-500/20 text-blue-400 px-2 py-0.5 rounded font-bold uppercase">{selectedElement.concept.code}</span>
                        <div className="text-[11px] font-medium text-theme-text-primary leading-snug mt-1.5">{selectedElement.concept.description}</div>
                      </div>

                      <div className="grid grid-cols-2 gap-2.5 text-[10px] border-t border-theme-border/60 pt-2.5">
                        <div>Unidad: <span className="font-bold text-theme-text-primary">{selectedElement.concept.unit}</span></div>
                        <div>Precio: <span className="font-bold text-theme-text-primary">${parseFloat(selectedElement.concept.unit_price).toLocaleString()}</span></div>
                      </div>

                      <div className="space-y-1.5 border-t border-theme-border/60 pt-2.5">
                        <div className="flex justify-between text-[10px]">
                          <span className="text-theme-text-secondary">Contratado:</span>
                          <span className="font-bold text-theme-text-primary">{parseFloat(selectedElement.concept.contract_qty).toLocaleString()} {selectedElement.concept.unit}</span>
                        </div>
                        <div className="flex justify-between text-[10px]">
                          <span className="text-theme-text-secondary">Ejecutado:</span>
                          <span className="font-bold text-green-500">{parseFloat(selectedElement.concept.executed_qty).toLocaleString()} {selectedElement.concept.unit}</span>
                        </div>
                        <div className="w-full h-1.5 bg-theme-border rounded-full overflow-hidden mt-1">
                          <div 
                            style={{ width: `${Math.min(100, (parseFloat(selectedElement.concept.executed_qty) / parseFloat(selectedElement.concept.contract_qty)) * 100)}%` }} 
                            className="h-full bg-green-500 rounded-full" 
                          />
                        </div>
                      </div>

                      <div className="flex justify-between text-[11px] font-bold border-t border-theme-border/60 pt-2.5">
                        <span className="text-theme-text-secondary">Importe Ejecutado:</span>
                        <span className="text-theme-text-primary">${parseFloat(selectedElement.concept.executed_amount).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-theme-text-secondary italic border-t border-theme-border pt-4">
                    Sin concepto de estimación asociado.
                  </div>
                )}

              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-center p-4 text-theme-text-secondary">
                <HelpCircle size={48} className="mb-4 text-theme-border" />
                <h4 className="font-bold text-theme-text-primary text-sm">Exploración Interactiva</h4>
                <p className="text-[11px] leading-relaxed mt-1 text-theme-text-secondary">
                  Haz clic en cualquier elemento del modelo 3D (Zapatas, Columnas, Losas o Muros) para inspeccionar sus vinculaciones con la planificación física de Gantt y los reportes de estimación financiera.
                </p>
              </div>
            )}

            {/* Quick General Summary Footer Card */}
            {!selectedElement && (
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-2xl p-4 text-[10px] text-blue-400 space-y-1.5">
                <div className="font-bold flex items-center gap-1.5 uppercase tracking-wider text-xs">
                  <Info size={14} /> Información de Modelado
                </div>
                <p className="leading-relaxed">
                  Las cimentaciones y el Nivel 1 se encuentran al 100% de ejecución. El Nivel 2 tiene sus columnas completadas y la losa de entrepiso en proceso de vaciado.
                </p>
              </div>
            )}
          </div>

        </div>

      </div>
    </div>
  );
};

export default BimViewer;
